import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLoading,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
  useIonViewDidEnter,
} from '@ionic/react';
import {
  arrowBack,
  calendarOutline,
  cashOutline,
  checkmarkCircleOutline,
  closeOutline,
  copyOutline,
  informationCircleOutline,
  peopleOutline,
  timeOutline,
  trendingUpOutline,
} from 'ionicons/icons';
import React, { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { MessageContext } from '../../../../../contexts/MessageContext';
import { parceiroIndicacoes, requestService } from '../../../../../Utils/Services';
import './Parceiro.css';

interface IndicacaoParceiro {
  id: string;
  empresaNome: string;
  responsavelNome?: string;
  dataInicio: string;
  mensalidadePagaNoMesEntrada: boolean;
  dataPagamentoMensalidade?: string | null;
  valorMensalidadePaga: number;
}

interface ResumoParceiro {
  totalIndicados: number;
  indicadosQuePagaram: number;
  aguardandoPrimeiraMensalidade: number;
  conversaoPercentual: number;
  valorComissoesGeradas: number;
}

interface PainelParceiro {
  mes: string;
  conviteParceiro: string;
  resumo: ResumoParceiro;
  indicacoes: IndicacaoParceiro[];
}

const normalizarPainelParceiro = (response: any): PainelParceiro => ({
  ...response,
  resumo: {
    ...response?.resumo,
    indicadosQuePagaram: response?.resumo?.indicadosQuePagaram ?? response?.resumo?.pagaramNoMes ?? 0,
    aguardandoPrimeiraMensalidade:
      response?.resumo?.aguardandoPrimeiraMensalidade ?? response?.resumo?.aguardandoPagamento ?? 0,
    valorComissoesGeradas: response?.resumo?.valorComissoesGeradas ?? response?.resumo?.valorPagoNoMes ?? 0,
  },
  indicacoes: (response?.indicacoes || []).map((indicacao: any) => ({
    ...indicacao,
    mensalidadePagaNoMesEntrada:
      indicacao.mensalidadePagaNoMesEntrada ?? indicacao.pagoNoMesEntrada ?? false,
    dataPagamentoMensalidade:
      indicacao.dataPagamentoMensalidade ?? indicacao.dataPagamentoNoMes ?? null,
    valorMensalidadePaga: indicacao.valorMensalidadePaga ?? indicacao.valorPagoNoMes ?? 0,
  })),
});

const resumoVazio: ResumoParceiro = {
  totalIndicados: 0,
  indicadosQuePagaram: 0,
  aguardandoPrimeiraMensalidade: 0,
  conversaoPercentual: 0,
  valorComissoesGeradas: 0,
};

const obterMesAtual = () => {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
};

const mesesDoAno = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const obterAnosDisponiveis = () => {
  const anoAtual = new Date().getFullYear();
  const primeiroAno = 2000;
  return Array.from({ length: anoAtual - primeiroAno + 1 }, (_, index) => anoAtual - index);
};

const formatarData = (data?: string | null) => {
  if (!data) return '—';

  const dataNormalizada = data.length === 10 ? `${data}T00:00:00` : data;
  const valor = new Date(dataNormalizada);
  return Number.isNaN(valor.getTime()) ? '—' : valor.toLocaleDateString('pt-BR');
};

const formatarMoeda = (valor = 0) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);

const Parceiro: React.FC = () => {
  const history = useHistory();
  const { setEsconderMenu, setNomeMenu } = useContext(MessageContext);
  const [mes, setMes] = useState(obterMesAtual());
  const [dados, setDados] = useState<PainelParceiro | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [copiado, setCopiado] = useState(false);
  const [mostrarRegras, setMostrarRegras] = useState(false);

  const acessos = useMemo(
    () => JSON.parse(window.localStorage.getItem('acessos') || '{}'),
    []
  );
  const podeAcessar = acessos?.parceiro === 'leitura' || acessos?.parceiro === 'edicao';

  const carregar = async (mesReferencia = mes, eventoRefresh?: CustomEvent) => {
    if (!podeAcessar) {
      eventoRefresh?.detail.complete();
      history.replace('/empresa/inicio');
      return;
    }

    const empresaId = window.localStorage.getItem('empresa') || '';
    if (!empresaId) {
      setErro('Não foi possível identificar a empresa vinculada ao usuário.');
      eventoRefresh?.detail.complete();
      return;
    }

    setCarregando(!eventoRefresh);
    setErro('');

    await requestService(
      parceiroIndicacoes.url
        .replace('{id}', empresaId.replace(/#/g, '%23'))
        .concat(`?mes=${encodeURIComponent(mesReferencia)}`),
      {
        method: parceiroIndicacoes.method,
        headers: [['token', window.localStorage.getItem('token')]],
        credentials: 'include',
      },
      history,
      (response: any) => {
        setDados(normalizarPainelParceiro(response));
        setCarregando(false);
        eventoRefresh?.detail.complete();
      },
      (error: any) => {
        setErro(error?.message || 'Não foi possível carregar as indicações deste período.');
        setCarregando(false);
        eventoRefresh?.detail.complete();
      }
    );
  };

  useIonViewDidEnter(() => {
    setEsconderMenu(true);
    setNomeMenu('');

    if (!podeAcessar) {
      history.replace('/empresa/inicio');
      return;
    }

    carregar();
  });

  const copiarConvite = async () => {
    if (!dados?.conviteParceiro) return;

    try {
      await navigator.clipboard.writeText("https://xeguei.com.br/?convite=" + dados.conviteParceiro);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1800);
    } catch {
      setCopiado(false);
    }
  };

  const resumo = dados?.resumo || resumoVazio;
  const indicacoes = dados?.indicacoes || [];
  const [anoSelecionado, numeroMesSelecionado] = mes.split('-').map(Number);
  const anoAtual = new Date().getFullYear();
  const mesAtual = new Date().getMonth() + 1;

  const alterarAno = (ano: number) => {
    const mesPermitido = ano === anoAtual
      ? Math.min(numeroMesSelecionado, mesAtual)
      : numeroMesSelecionado;
    const novoPeriodo = `${ano}-${String(mesPermitido).padStart(2, '0')}`;
    setMes(novoPeriodo);
    carregar(novoPeriodo);
  };

  const alterarMes = (numeroMes: number) => {
    const novoPeriodo = `${anoSelecionado}-${String(numeroMes).padStart(2, '0')}`;
    setMes(novoPeriodo);
    carregar(novoPeriodo);
  };

  return (
    <IonPage className="parceiro-page">
      <IonHeader className="ion-no-border">
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton onClick={() => history.replace('/empresa/inicio')} aria-label="Voltar">
              <IonIcon slot="icon-only" icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Parceiro</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setMostrarRegras(true)} aria-label="Ver regras do programa de parceiros">
              <IonIcon slot="icon-only" icon={informationCircleOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen>
        <IonRefresher slot="fixed" onIonRefresh={(event) => carregar(mes, event)}>
          <IonRefresherContent pullingText="Puxe para atualizar" refreshingSpinner="crescent" />
        </IonRefresher>

        <main className="parceiro-container">
          <section className="parceiro-hero">
            <div>
              <span className="parceiro-eyebrow">PROGRAMA DE PARCEIROS</span>
              <h1>Acompanhe suas indicações</h1>
              <p>Veja quem entrou com o seu convite e quem realizou o pagamento no mês de entrada.</p>
            </div>

            {dados?.conviteParceiro && (
              <button className="parceiro-convite" type="button" onClick={copiarConvite}>
                <span>Seu convite</span>
                <strong>{dados.conviteParceiro}</strong>
                <IonIcon icon={copyOutline} />
                <small>{copiado ? 'Copiado!' : 'Clique para copiar'}</small>
              </button>
            )}
          </section>

          <section className="parceiro-filtros" aria-label="Filtros das indicações">
            <div className="parceiro-periodo">
              <IonIcon icon={calendarOutline} />
              <div className="parceiro-seletor-periodo">
                <label>Mês de entrada</label>
                <IonSelect
                  aria-label="Mês de entrada"
                  interface="popover"
                  value={numeroMesSelecionado}
                  onIonChange={(event) => alterarMes(Number(event.detail.value))}
                >
                  {mesesDoAno.map((nomeMes, index) => {
                    const numeroMes = index + 1;
                    return (
                      <IonSelectOption
                        key={nomeMes}
                        value={numeroMes}
                        disabled={anoSelecionado === anoAtual && numeroMes > mesAtual}
                      >
                        {nomeMes}
                      </IonSelectOption>
                    );
                  })}
                </IonSelect>
              </div>
              <div className="parceiro-seletor-periodo">
                <label>Ano</label>
                <IonSelect
                  aria-label="Ano de entrada"
                  interface="popover"
                  value={anoSelecionado}
                  onIonChange={(event) => alterarAno(Number(event.detail.value))}
                >
                  {obterAnosDisponiveis().map((ano) => (
                    <IonSelectOption key={ano} value={ano}>{ano}</IonSelectOption>
                  ))}
                </IonSelect>
              </div>
            </div>
          </section>

          {erro && (
            <section className="parceiro-alerta" role="alert">
              <IonText color="danger">{erro}</IonText>
              <IonButton fill="clear" size="small" onClick={() => carregar(mes)}>Tentar novamente</IonButton>
            </section>
          )}

          <section className="parceiro-resumo" aria-label="Resumo do período">
            <article className="parceiro-card-resumo">
              <span className="parceiro-card-icon"><IonIcon icon={peopleOutline} /></span>
              <div><small>Indicados</small><strong>{resumo.totalIndicados}</strong></div>
            </article>
            <article className="parceiro-card-resumo parceiro-card-pago">
              <span className="parceiro-card-icon"><IonIcon icon={checkmarkCircleOutline} /></span>
              <div><small>Indicados que pagaram</small><strong>{resumo.indicadosQuePagaram}</strong></div>
            </article>
            <article className="parceiro-card-resumo parceiro-card-pendente">
              <span className="parceiro-card-icon"><IonIcon icon={timeOutline} /></span>
              <div><small>Aguardando 1ª mensalidade</small><strong>{resumo.aguardandoPrimeiraMensalidade}</strong></div>
            </article>
            <article className="parceiro-card-resumo parceiro-card-conversao">
              <span className="parceiro-card-icon"><IonIcon icon={trendingUpOutline} /></span>
              <div><small>Conversão</small><strong>{resumo.conversaoPercentual.toFixed(1)}%</strong></div>
            </article>
          </section>

          <section className="parceiro-lista-card">
            <header className="parceiro-lista-header">
              <div>
                <h2>Empresas indicadas</h2>
                <p>O pagamento considera apenas mensalidades aprovadas no mesmo mês da entrada.</p>
              </div>
              <div className="parceiro-valor-pago">
                <IonIcon icon={cashOutline} />
                <span>Comissões geradas<strong>{formatarMoeda(resumo.valorComissoesGeradas)}</strong></span>
              </div>
            </header>

            {indicacoes.length === 0 && !carregando ? (
              <div className="parceiro-vazio">
                <IonIcon icon={peopleOutline} />
                <h3>Nenhuma indicação neste mês</h3>
                <p>Quando uma empresa usar o seu convite, ela aparecerá aqui.</p>
              </div>
            ) : (
              <div className="parceiro-tabela-wrapper">
                <table className="parceiro-tabela">
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th>Responsável</th>
                      <th>Entrada</th>
                      <th>Primeira mensalidade</th>
                      <th>Data da mensalidade</th>
                      <th className="ion-text-end">Comissão gerada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indicacoes.map((indicacao) => (
                      <tr key={indicacao.id}>
                        <td data-label="Empresa"><strong>{indicacao.empresaNome}</strong></td>
                        <td data-label="Responsável">{indicacao.responsavelNome || '—'}</td>
                        <td data-label="Entrada">{formatarData(indicacao.dataInicio)}</td>
                        <td data-label="Primeira mensalidade">
                          <span className={`parceiro-status ${indicacao.mensalidadePagaNoMesEntrada ? 'pago' : 'pendente'}`}>
                            <IonIcon icon={indicacao.mensalidadePagaNoMesEntrada ? checkmarkCircleOutline : timeOutline} />
                            {indicacao.mensalidadePagaNoMesEntrada ? 'Mensalidade paga' : 'Aguardando mensalidade'}
                          </span>
                        </td>
                        <td data-label="Data da mensalidade">{formatarData(indicacao.dataPagamentoMensalidade)}</td>
                        <td data-label="Comissão gerada" className="ion-text-end parceiro-coluna-valor">
                          {indicacao.mensalidadePagaNoMesEntrada ? formatarMoeda(indicacao.valorMensalidadePaga) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </IonContent>

      <IonModal
        className="parceiro-regras-modal"
        isOpen={mostrarRegras}
        onDidDismiss={() => setMostrarRegras(false)}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>Regras do programa</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setMostrarRegras(false)} aria-label="Fechar regras">
                <IonIcon slot="icon-only" icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div className="parceiro-regras-conteudo">
            <div className="parceiro-regras-intro">
              <span><IonIcon icon={informationCircleOutline} /></span>
              <div>
                <h2>Como funciona</h2>
                <p>Indique novos clientes e aproveite os benefícios do Programa de Parceiros Xeguei.</p>
              </div>
            </div>

            <ol className="parceiro-regras-lista">
              <li>
                <span>1</span>
                <div>
                  <strong>Mensalidade gratuita</strong>
                  <p>Ao manter pelo menos 3 clientes indicados com a assinatura ativa, você não paga a mensalidade do Xeguei.</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Primeira mensalidade</strong>
                  <p>Você recebe o valor da primeira mensalidade de cada cliente indicado após a confirmação do pagamento.</p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Data do repasse</strong>
                  <p>O repasse será realizado no dia 10 do mês seguinte à confirmação do pagamento.</p>
                </div>
              </li>
            </ol>

            <div className="parceiro-regras-exemplo">
              <IonIcon icon={calendarOutline} />
              <p><strong>Exemplo:</strong> se o cliente pagar em agosto, o repasse será feito em 10 de setembro.</p>
            </div>
          </div>
        </IonContent>
      </IonModal>

      <IonLoading isOpen={carregando} message="Carregando indicações..." />
    </IonPage>
  );
};

export default Parceiro;
